import { useRecordContext, useNotify } from "ra-core";
import { cn } from "@/lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ChevronDownIcon } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import type { Contact, LeadSource } from "../types";
import { useSetLeadSource } from "./useSetLeadSource";

const NONE_VALUE = "__none__";

// Same 8 values as the sidebar Lead Source filter (ContactListFilter.tsx) — kept as
// a separate literal list rather than a shared import so the filter's labels (tuned
// for a list of toggle buttons) and this selector's labels (a dropdown, same values)
// can diverge later without one editor accidentally breaking the other's copy.
const LEAD_SOURCE_OPTIONS: { value: LeadSource; label: string }[] = [
  { value: "linkedin_engagement", label: "LinkedIn engagement" },
  { value: "linkedin_network", label: "LinkedIn network" },
  { value: "linkedin_jobs", label: "LinkedIn jobs" },
  { value: "reddit", label: "Reddit" },
  { value: "google_places", label: "Google Places" },
  { value: "chatbot_handoff", label: "Chatbot handoff" },
  { value: "lead_magnet", label: "Lead magnet" },
  { value: "cold_outbound", label: "Cold outbound" },
];

type LeadSourceSelectorProps = {
  disabled?: boolean;
  leadSource?: LeadSource | null;
  setLeadSource: (value: LeadSource | null) => void;
  triggerClassName?: string;
};

/**
 * Same shape as notes/StatusSelector.tsx (native <select> on mobile for
 * accessibility/performance, Radix Select on desktop) — deliberately mirrored
 * rather than sharing a generic component, since Status carries per-option
 * color dots this doesn't need.
 */
export const LeadSourceSelector = ({
  disabled,
  leadSource,
  setLeadSource,
  triggerClassName,
}: LeadSourceSelectorProps) => {
  const isMobile = useIsMobile();
  const noneLabel = "None";

  if (isMobile) {
    const selectedOption = LEAD_SOURCE_OPTIONS.find(
      (o) => o.value === leadSource,
    );
    return (
      <div className={cn("relative", "w-32", triggerClassName)}>
        <div
          aria-hidden="true"
          className={cn(
            "border-input flex w-full items-center justify-between gap-2 rounded-md border bg-transparent px-3 py-2 text-sm whitespace-nowrap shadow-xs h-9",
            disabled && "cursor-not-allowed opacity-50",
          )}
        >
          <span className="line-clamp-1">
            {selectedOption ? selectedOption.label : noneLabel}
          </span>
          <ChevronDownIcon className="size-4 opacity-50 shrink-0" />
        </div>
        <select
          disabled={disabled}
          value={leadSource || ""}
          onChange={(e) =>
            setLeadSource((e.target.value || null) as LeadSource | null)
          }
          aria-label="Lead source"
          className="absolute inset-0 opacity-0 w-full h-full cursor-pointer"
        >
          <option value="">{noneLabel}</option>
          {LEAD_SOURCE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
    );
  }

  const handleValueChange = (value: string) => {
    setLeadSource(value === NONE_VALUE ? null : (value as LeadSource));
  };

  return (
    <Select
      disabled={disabled}
      value={leadSource || NONE_VALUE}
      onValueChange={handleValueChange}
    >
      <SelectTrigger className={cn("w-32", triggerClassName)}>
        <SelectValue placeholder={noneLabel} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={NONE_VALUE}>{noneLabel}</SelectItem>
        {LEAD_SOURCE_OPTIONS.map((opt) => (
          <SelectItem key={opt.value} value={opt.value}>
            {opt.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

/**
 * PRD #16 §F's manual lead_source setter, now built. Writes
 * mentorax.lead_snapshots directly (useSetLeadSource — not the contacts
 * resource, lead_source isn't a public.contacts column). Works whether the
 * value is unset, or already set by Marco/a workflow — this is an override,
 * not a create-only affordance, matching the Status dropdown right above it.
 */
export const ContactLeadSourceSelector = () => {
  const record = useRecordContext<Contact>();
  const notify = useNotify();
  const { mutate: setLeadSource, isPending } = useSetLeadSource();
  if (!record) return null;

  const handleChange = (next: LeadSource | null) => {
    if (next === (record.lead_source ?? null)) return;

    setLeadSource(
      {
        contactId: Number(record.id),
        companyId: record.company_id ? Number(record.company_id) : null,
        leadSource: next,
      },
      {
        onError: (error: any) => {
          notify(error?.message || "ra.notification.http_error", {
            type: "error",
          });
        },
      },
    );
  };

  return (
    <div className="[&_button]:w-auto">
      <LeadSourceSelector
        disabled={isPending}
        leadSource={record.lead_source}
        setLeadSource={handleChange}
        triggerClassName="w-full"
      />
      {!record.lead_source && (
        <p className="text-muted-foreground text-xs italic mt-1">
          Not set by a workflow yet — set it manually, or leave it for Marco.
        </p>
      )}
    </div>
  );
};
